/**
 * Authentication Store
 *
 * Pinia store for managing authentication state
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { AuthTokens, CognitoUser, UserRole, AuthState } from '@/types';
import { saveLastEmail, getLastEmail } from '@/utils';

export const useAuthStore = defineStore('auth', () => {
  // State
  const email = ref<string | null>(null);
  const user = ref<CognitoUser | null>(null);
  const tokens = ref<AuthTokens | null>(null);
  const roles = ref<UserRole[]>([]);
  const referrerUrl = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => {
    return Boolean(user.value && tokens.value);
  });

  const userRoles = computed(() => {
    return roles.value;
  });

  const hasReferrer = computed(() => {
    return Boolean(referrerUrl.value);
  });

  const userEmail = computed(() => {
    return user.value?.email || email.value;
  });

  const userId = computed(() => {
    return user.value?.userId;
  });

  // Actions
  function setEmail(newEmail: string | null) {
    email.value = newEmail;
    if (newEmail) {
      saveLastEmail(newEmail);
    }
  }

  function setUser(newUser: CognitoUser | null) {
    user.value = newUser;
    if (newUser) {
      roles.value = newUser.roles;
      email.value = newUser.email;
    }
  }

  function setTokens(newTokens: AuthTokens | null) {
    tokens.value = newTokens;
  }

  function setRoles(newRoles: UserRole[]) {
    roles.value = newRoles;
  }

  function setReferrerUrl(url: string | null) {
    referrerUrl.value = url;
  }

  function clearAuth() {
    email.value = null;
    user.value = null;
    tokens.value = null;
    roles.value = [];
    referrerUrl.value = null;
  }

  function initializeFromStorage() {
    const lastEmail = getLastEmail();
    if (lastEmail && !email.value) {
      email.value = lastEmail;
    }
  }

  // Initialize on store creation
  initializeFromStorage();

  return {
    // State
    email,
    user,
    tokens,
    roles,
    referrerUrl,

    // Getters
    isAuthenticated,
    userRoles,
    hasReferrer,
    userEmail,
    userId,

    // Actions
    setEmail,
    setUser,
    setTokens,
    setRoles,
    setReferrerUrl,
    clearAuth,
    initializeFromStorage,
  };
});